import endpoints from '@/constants/endpoints';
import axiosV2 from '@/data/api/axiosv2';
import { ITEMS_TABLE_PAGE_LIMIT } from '@/utils/constants';
import {
  BuyerAddressesResponse,
  BuyerAddressRequest,
  BuyerContactDataResponse,
  BuyerDetailsResponse,
  BuyerFiscalDataResponse,
  BuyersResponse,
  UpdateBuyerContactDataRequest,
  UpdateBuyerFiscalDataRequest,
} from '../model/buyers/BuyersDto';

export const getBuyers = async (args: {
  pageNumber?: number;
  searchTerm?: string;
}): Promise<BuyersResponse> => {
  const endpoint = endpoints.buyer.getBuyers;

  const response = await axiosV2.get<BuyersResponse>(endpoint, {
    params: {
      limit: ITEMS_TABLE_PAGE_LIMIT,
      page: args.pageNumber,
      ...(args.searchTerm && { filter: `${args.searchTerm}` }),
    },
  });

  return response;
};

export const setTrustedBuyer = async (args: {
  userId: string;
  trusted: boolean;
}): Promise<void> => {
  const endpoint = endpoints.buyer.toggleTrusted.replace(
    ':userId',
    args.userId
  );
  await axiosV2.post(endpoint, { trusted: args.trusted });
  return;
};

export const getBuyerContactData = async (args: {
  buyerId: string;
}): Promise<BuyerContactDataResponse> => {
  const endpoint = endpoints.buyer.contactData.replace(':userId', args.buyerId);
  const response: BuyerContactDataResponse = await axiosV2.get(endpoint);
  return response;
};

export const updateBuyerContactData = async (args: {
  buyerId: string;
  updateBuyerContactDataRequest: UpdateBuyerContactDataRequest;
}) => {
  const endpoint = endpoints.buyer.updateContactData.replace(
    ':userId',
    args.buyerId
  );
  const response: BuyerContactDataResponse = await axiosV2.patch(
    endpoint,
    args.updateBuyerContactDataRequest
  );
  return response;
};

export const getBuyerFiscalData = async (args: {
  buyerId: string;
}): Promise<BuyerFiscalDataResponse> => {
  const endpoint = endpoints.buyer.fiscalData.replace(':userId', args.buyerId);
  const response: BuyerFiscalDataResponse = await axiosV2.get(endpoint);
  return response;
};

export const getBuyerDetails = async (args: {
  buyerId: string;
}): Promise<BuyerDetailsResponse> => {
  const endpoint = endpoints.buyer.details.replace(':userId', args.buyerId);
  const response: BuyerDetailsResponse = await axiosV2.get(endpoint);
  return response;
};

export const updateBuyerFiscalData = async (
  buyerId: string,
  request: UpdateBuyerFiscalDataRequest
): Promise<void> => {
  const endpoint = endpoints.buyer.updateFiscalData.replace(':userId', buyerId);
  return await axiosV2.patch(endpoint, request);
};

export const getBuyerAddresses = async (
  buyerId: string
): Promise<BuyerAddressesResponse> => {
  const endpoint = endpoints.buyer.getAddresses.replace(':userId', buyerId);
  const response: BuyerAddressesResponse = await axiosV2.get(endpoint);
  return response;
};

export const deleteBuyerAddress = async (
  buyerId: string,
  addressId: string
): Promise<void> => {
  const endpoint = endpoints.buyer.deleteAddress
    .replace(':userId', buyerId)
    .replace(':addressId', addressId);
  return await axiosV2.delete(endpoint);
};

export const addBuyerAddress = async (
  buyerId: string,
  request: BuyerAddressRequest
): Promise<void> => {
  const endpoint = endpoints.buyer.addAddress.replace(':userId', buyerId);
  return await axiosV2.post(endpoint, request);
};

export const setAddressAsFavorite = async (args: {
  buyerId: string;
  addressId: string;
}): Promise<void> => {
  const endpoint = endpoints.buyer.setFavoriteAddress
    .replace(':userId', args.buyerId)
    .replace(':addressId', args.addressId);
  return await axiosV2.post(endpoint);
};
